import { z } from 'zod';
import type { ResourceKind } from './dto.js';

/**
 * Resource sync layer contract (ADR-0010): reconciliation run bookkeeping,
 * drift findings and the sync status page DTOs.
 */

// ── Sync runs ───────────────────────────────────────────────────────

export const SYNC_RUN_TRIGGERS = ['scheduled', 'manual', 'webhook'] as const;
export type SyncRunTrigger = (typeof SYNC_RUN_TRIGGERS)[number];

export const SYNC_RUN_STATUSES = ['running', 'success', 'failed'] as const;
export type SyncRunStatus = (typeof SYNC_RUN_STATUSES)[number];

/** Counters a reconciliation run reports (persisted in sync_runs.stats). */
export interface SyncRunStats {
  /** Resources returned by Coolify's GET /resources. */
  resourcesSeen: number;
  /** In-flight deployments returned by GET /deployments. */
  deploymentsSeen: number;
  /** Ledger rows whose status/health changed this run. */
  statusUpdated: number;
  /** Ledger rows newly tombstoned (coolify_uuid gone upstream). */
  orphaned: number;
  /** Tombstoned rows whose Coolify resource reappeared (tombstone cleared). */
  relinked: number;
  /** New "unmanaged, adopt?" findings opened this run. */
  findingsOpened: number;
  /** Findings auto-resolved (resource gone or became ledger-managed). */
  findingsResolved: number;
  /** Subscriptions whose rolled-up health changed. */
  healthChanged: number;
}

export interface SyncRunDto {
  id: string;
  trigger: SyncRunTrigger;
  status: SyncRunStatus;
  startedAt: string;
  finishedAt: string | null;
  durationMs: number | null;
  error: string | null;
  stats: SyncRunStats | null;
}

// ── Drift findings ──────────────────────────────────────────────────

export const DRIFT_FINDING_STATUSES = ['open', 'dismissed'] as const;
export type DriftFindingStatus = (typeof DRIFT_FINDING_STATUSES)[number];

export interface DriftFindingDto {
  id: string;
  /** Coolify uuid of the unmanaged resource. */
  coolifyUuid: string;
  /** Raw Coolify type string ("application", "standalone-postgresql", …). */
  resourceType: string;
  /** Panel resource kind the type maps to (null when unrecognized). */
  kind: ResourceKind | null;
  name: string;
  projectUuid: string;
  subscriptionId: string | null;
  subscriptionName: string | null;
  clientName: string | null;
  /** Last status Coolify reported for the resource, normalized. */
  lastStatus: string | null;
  status: DriftFindingStatus;
  firstSeenAt: string;
  lastSeenAt: string;
  dismissedAt: string | null;
}

// ── Sync status page ────────────────────────────────────────────────

/** A tombstoned ledger row, as listed on the sync page. */
export interface OrphanedResourceDto {
  id: string;
  subscriptionId: string;
  subscriptionName: string;
  clientName: string;
  kind: ResourceKind;
  name: string;
  coolifyUuid: string | null;
  status: string;
  orphanedAt: string;
}

export interface SyncStatusDto {
  /** Most recent run (any outcome), null before the first one. */
  lastRun: SyncRunDto | null;
  /** A run is executing right now. */
  running: boolean;
  intervalMs: number;
  openFindings: number;
  dismissedFindings: number;
  orphanedResources: number;
  /** Whether a sync-hook secret exists (hook URL is admin-only, see /hook). */
  hookConfigured: boolean;
}

/** Admin-only: what to paste into Coolify's webhook settings. */
export interface SyncHookInfoDto {
  /** Panel-relative path including the token query parameter. */
  path: string;
  /** Absolute URL when the panel knows its public URL (APP_URL), else null. */
  url: string | null;
}

// ── Inputs ──────────────────────────────────────────────────────────

export const adoptFindingSchema = z.object({
  /** Optional rename applied to the adopted ledger row. */
  name: z.string().trim().min(1).max(200).optional(),
});
export type AdoptFindingInput = z.infer<typeof adoptFindingSchema>;

export const syncRunsQuerySchema = z.object({
  take: z.coerce.number().int().min(1).max(100).default(20),
});
export type SyncRunsQuery = z.infer<typeof syncRunsQuerySchema>;
